import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Grid,
    TextField,
    MenuItem,
    Card,
    CardContent,
    IconButton
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Expense } from '@starwave/shared/src/types'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

export default function ExpensesPage() {
    const [expenses, setExpenses] = useState<Expense[]>([])
    const [loading, setLoading] = useState(true)

    // Form State
    const [description, setDescription] = useState('')
    const [amount, setAmount] = useState('')
    const [type, setType] = useState('miscellaneous')

    useEffect(() => {
        const q = query(collection(db, 'expenses'), orderBy('date', 'desc'))
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Expense[]
            setExpenses(data)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!description || !amount) return

        try {
            await addDoc(collection(db, 'expenses'), {
                description,
                amount: Number(amount),
                type,
                date: serverTimestamp(),
                createdAt: serverTimestamp()
            })
            toast.success('Expense recorded')
            setDescription('')
            setAmount('')
            setType('miscellaneous')
        } catch (error) {
            toast.error('Failed to add expense')
        }
    }

    const handleDelete = async (id: string) => {
        if (confirm('Are you sure you want to delete this expense?')) {
            await deleteDoc(doc(db, 'expenses', id))
            toast.success('Expense deleted')
        }
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Expense Tracking
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Add New Expense</Typography>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Description"
                                    fullWidth
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    required
                                />
                                <TextField
                                    label="Amount"
                                    type="number"
                                    fullWidth
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    required
                                />
                                <TextField
                                    select
                                    label="Type"
                                    fullWidth
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <MenuItem value="rent">Rent</MenuItem>
                                    <MenuItem value="salary">Salary</MenuItem>
                                    <MenuItem value="utilities">Utilities</MenuItem>
                                    <MenuItem value="transport">Transport</MenuItem>
                                    <MenuItem value="internet">Internet</MenuItem>
                                    <MenuItem value="miscellaneous">Miscellaneous</MenuItem>
                                </TextField>
                                <Button type="submit" variant="contained" startIcon={<AddIcon />}>
                                    Add Expense
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {expenses.map((expense) => (
                                        <TableRow key={expense.id}>
                                            <TableCell>
                                                {expense.date ? format(expense.date.toDate(), 'MMM dd, yyyy') : 'N/A'}
                                            </TableCell>
                                            <TableCell>{expense.description}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{expense.type}</TableCell>
                                            <TableCell align="right">PKR {expense.amount.toLocaleString()}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="error" onClick={() => handleDelete(expense.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {expenses.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No expenses recorded</TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total Expenses:</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            PKR {expenses.reduce((acc, curr) => acc + curr.amount, 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    )
}
